import type { Mode, ReportEvidencePayload } from "./types"

const evidenceDurationMs = 10_000
const maxEvidenceBytes = 3 * 1024 * 1024

export async function captureReportEvidence(stream: MediaStream | null | undefined, mode: Mode): Promise<ReportEvidencePayload | null> {
  if (!stream || typeof MediaRecorder === "undefined") return null
  const tracks = mode === "video" ? stream.getTracks() : stream.getAudioTracks()
  if (!tracks.some((track) => track.readyState === "live")) return null

  const evidenceStream = new MediaStream(tracks.filter((track) => track.readyState === "live"))
  const mimeType = preferredEvidenceMimeType(mode)
  const recorder = new MediaRecorder(evidenceStream, {
    ...(mimeType ? { mimeType } : {}),
    bitsPerSecond: mode === "video" ? 260_000 : 64_000,
  })
  const chunks: Blob[] = []

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      if (recorder.state !== "inactive") recorder.stop()
    }, evidenceDurationMs)

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data)
    }
    recorder.onerror = () => {
      window.clearTimeout(timeout)
      reject(new Error("report evidence capture failed"))
    }
    recorder.onstop = async () => {
      window.clearTimeout(timeout)
      const evidenceMimeType = safeEvidenceMimeType(recorder.mimeType || mimeType || fallbackMimeType(mode), mode)
      const blob = new Blob(chunks, { type: evidenceMimeType })
      if (!blob.size) {
        resolve(null)
        return
      }
      if (blob.size > maxEvidenceBytes) {
        reject(new Error("report evidence is too large"))
        return
      }
      const dataUrl = await blobToDataUrl(blob)
      resolve({
        kind: mode === "video" ? "video" : "audio",
        mimeType: evidenceMimeType,
        dataUrl,
        sizeBytes: blob.size,
        durationSeconds: Math.round(evidenceDurationMs / 1000),
        capturedAt: new Date().toISOString(),
      })
    }
    recorder.start(1000)
  })
}

function preferredEvidenceMimeType(mode: Mode): string {
  const candidates = mode === "video"
    ? ["video/webm;codecs=vp8,opus", "video/webm"]
    : ["audio/webm;codecs=opus", "audio/webm"]
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || ""
}

function fallbackMimeType(mode: Mode): string {
  return mode === "video" ? "video/webm" : "audio/webm"
}

function safeEvidenceMimeType(value: string, mode: Mode): string {
  const normalized = value.trim().toLowerCase()
  if (normalized.startsWith("video/")) return "video/webm"
  if (normalized.startsWith("audio/")) return "audio/webm"
  return fallbackMimeType(mode)
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error("report evidence could not be read"))
    reader.onload = () => resolve(String(reader.result || ""))
    reader.readAsDataURL(blob)
  })
}
